import React, { useEffect, useMemo } from "react";
import { ClientMeal, MealSelection, MEAL_TYPE_ICON, MEAL_TYPE_LABEL } from "../../utils/api/mealsAPI";
import { ApartmentMealSetting } from "../../utils/api/mealsAPI";

interface CalDate { year: number; month: number; day: number }

interface Props {
  aptMeals: ApartmentMealSetting[];
  checkIn: CalDate;
  checkOut: CalDate;
  currency: string;
  capacity?: number;
  onChange: (selections: MealSelection[], total: number) => void;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const toDate = (d: CalDate) => new Date(Date.UTC(d.year, d.month - 1, d.day));
const pad2 = (n: number) => String(n).padStart(2, "0");
const DAY_SHORT = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];

const fmtDay = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${DAY_SHORT[dow]}, ${pad2(d)}.${pad2(m)}`;
};

const fmtPrice = (price: number, currency: string) => {
  if (currency === "EUR") return `€${price % 1 === 0 ? price : price.toFixed(2)}`;
  if (currency === "BAM") return `${price} KM`;
  return `${price} RSD`;
};

const stayDates = (ci: CalDate, co: CalDate): string[] => {
  const dates: string[] = [];
  let cur = toDate(ci);
  const last = toDate(co);
  while (cur < last) {
    dates.push(`${cur.getUTCFullYear()}-${pad2(cur.getUTCMonth() + 1)}-${pad2(cur.getUTCDate())}`);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return dates;
};

const getMeal = (s: ApartmentMealSetting): ClientMeal | null =>
  typeof s.mealId === "object" && s.mealId !== null ? (s.mealId as ClientMeal) : null;

// ─── State types ──────────────────────────────────────────────────────────────
// Item-mode:  key `${mealId}__${date}` → itemId[] (one per guest, "" = skip)
type SelectionMap = Record<string, string[]>;
// Per-person mode: key `${mealId}__${date}` → number of persons (0 = not ordered)
type PersonCountMap = Record<string, number>;

// ─── Stepper button ───────────────────────────────────────────────────────────
const StepBtn: React.FC<{ label: string; disabled?: boolean; onClick: () => void }> = ({ label, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      width: 30, height: 30,
      border: "1.5px solid #e8e5f0",
      borderRadius: 8,
      background: disabled ? "#faf9fd" : "#fff",
      color: disabled ? "#c4c0d0" : "#1a1520",
      fontSize: 16, fontWeight: 600,
      cursor: disabled ? "default" : "pointer",
      flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      lineHeight: 1,
    }}
  >
    {label}
  </button>
);

// ─── Small toggle switch ───────────────────────────────────────────────────────
const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={(e) => { e.stopPropagation(); onChange(); }}
    style={{
      position: "relative",
      width: 34, height: 20,
      borderRadius: 99, border: "none",
      background: checked ? "#5b50d6" : "#d1cde6",
      cursor: "pointer", flexShrink: 0,
      transition: "background 0.18s",
      padding: 0,
    }}
  >
    <span style={{
      position: "absolute",
      top: 3, left: checked ? 17 : 3,
      width: 14, height: 14,
      borderRadius: 99, background: "#fff",
      boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
      transition: "left 0.18s",
      display: "block",
    }} />
  </button>
);

// ─── Component ────────────────────────────────────────────────────────────────

const MealSelector: React.FC<Props> = ({ aptMeals, checkIn, checkOut, currency, capacity = 10, onChange }) => {
  const [selected, setSelected] = React.useState<SelectionMap>({});
  const [personCounts, setPersonCounts] = React.useState<PersonCountMap>({});
  const [activeMeals, setActiveMeals] = React.useState<Record<string, boolean>>({});

  const meals = useMemo(
    () =>
      aptMeals
        .map((s) => ({ setting: s, meal: getMeal(s) }))
        .filter((e): e is { setting: ApartmentMealSetting; meal: ClientMeal } =>
          e.meal !== null && e.meal.isActive
        ),
    [aptMeals]
  );

  const dates = useMemo(() => stayDates(checkIn, checkOut), [checkIn, checkOut]);

  const getSlots = (mealId: string, date: string): string[] =>
    selected[`${mealId}__${date}`] ?? [""];

  const getPersonCount = (mealId: string, date: string): number =>
    personCounts[`${mealId}__${date}`] ?? 0;

  // Recompute total whenever selections change
  useEffect(() => {
    const selections: MealSelection[] = [];
    let total = 0;

    for (const { setting, meal } of meals) {
      const isPerPerson = (setting.pricePerPerson ?? 0) > 0;

      for (const date of dates) {
        if (isPerPerson) {
          const count = getPersonCount(meal._id, date);
          if (count === 0) continue;
          const price = setting.includedInPrice ? 0 : count * setting.pricePerPerson!;
          total += price;
          selections.push({
            date,
            mealId: meal._id,
            mealName: meal.name,
            itemId: "",
            itemName: meal.name,
            persons: count,
            price,
            isIncluded: setting.includedInPrice,
          });
        } else {
          const slots = getSlots(meal._id, date);
          const nonEmpty = slots.filter(Boolean);
          if (nonEmpty.length === 0) continue;

          let dayPrice = 0;
          const personItems: Array<{ itemId: string; itemName: string }> = [];

          for (const itemId of nonEmpty) {
            const item = meal.items.find((it) => (it._id || it.name) === itemId);
            if (!item) continue;
            const unitPrice = setting.includedInPrice ? 0 : item.price;
            dayPrice += unitPrice;
            personItems.push({ itemId, itemName: item.name });
          }

          total += dayPrice;
          selections.push({
            date,
            mealId: meal._id,
            mealName: meal.name,
            itemId: personItems[0]?.itemId ?? "",
            itemName: personItems[0]?.itemName ?? "",
            persons: nonEmpty.length,
            price: dayPrice,
            isIncluded: setting.includedInPrice,
          });
        }
      }
    }

    onChange(selections, total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, personCounts, meals, dates]);

  if (meals.length === 0 || dates.length === 0) return null;

  // ── item-mode helpers ────────────────────────────────────────────────────────
  const setSlot = (mealId: string, date: string, idx: number, itemId: string) => {
    const key = `${mealId}__${date}`;
    setSelected((prev) => {
      const slots = [...(prev[key] ?? [""])];
      slots[idx] = itemId;
      return { ...prev, [key]: slots };
    });
  };

  const addGuest = (mealId: string, date: string) => {
    const key = `${mealId}__${date}`;
    setSelected((prev) => {
      const slots = prev[key] ?? [""];
      if (slots.length >= capacity) return prev;
      return { ...prev, [key]: [...slots, ""] };
    });
  };

  const removeGuest = (mealId: string, date: string, idx: number) => {
    const key = `${mealId}__${date}`;
    setSelected((prev) => {
      const slots = [...(prev[key] ?? [""])];
      slots.splice(idx, 1);
      return { ...prev, [key]: slots.length > 0 ? slots : [""] };
    });
  };

  // ── per-person helper ────────────────────────────────────────────────────────
  const changeCount = (mealId: string, date: string, delta: number) => {
    const key = `${mealId}__${date}`;
    setPersonCounts((prev) => {
      const next = Math.max(0, Math.min(capacity, (prev[key] ?? 0) + delta));
      return { ...prev, [key]: next };
    });
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1520", marginBottom: 8 }}>
        🍽️ Obroci
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {meals.map(({ setting, meal }) => {
          const isPerPerson = (setting.pricePerPerson ?? 0) > 0;
          const availableItems = meal.items.filter((it) => it.isAvailable);
          const minPrice = availableItems.length > 0
            ? Math.min(...availableItems.map((it) => it.price))
            : 0;

          const subtitlePrice = setting.includedInPrice
            ? "uključen u cijenu"
            : isPerPerson
            ? `${fmtPrice(setting.pricePerPerson!, currency)} / os.`
            : availableItems.length > 0
            ? `od ${fmtPrice(minPrice, currency)} / os.`
            : MEAL_TYPE_LABEL[meal.type];

          const badgeLabel = setting.includedInPrice
            ? "✓ uključeno"
            : isPerPerson
            ? `${fmtPrice(setting.pricePerPerson!, currency)} / os.`
            : `od ${fmtPrice(minPrice, currency)}`;

          const isActive = !!activeMeals[meal._id];

          return (
            <div
              key={meal._id}
              style={{
                border: `1.5px solid ${isActive ? "#c4bef7" : "#e8e5f0"}`,
                borderRadius: 10, overflow: "hidden",
                transition: "border-color 0.18s",
              }}
            >
              {/* Meal header */}
              <div
                onClick={() => setActiveMeals((prev) => ({ ...prev, [meal._id]: !prev[meal._id] }))}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 12px",
                  background: isActive ? "#f8f7ff" : "#faf9fd",
                  borderBottom: isActive ? "1px solid #e8e5f0" : "none",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <span style={{ fontSize: 17 }}>{MEAL_TYPE_ICON[meal.type]}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1520" }}>{meal.name}</div>
                  <div style={{ fontSize: 11, color: "#9893a8" }}>{subtitlePrice}</div>
                </div>

                {isActive && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                    background: setting.includedInPrice ? "rgba(22,163,74,0.1)" : "rgba(91,80,214,0.08)",
                    color: setting.includedInPrice ? "#16a34a" : "#5b50d6",
                    border: `1px solid ${setting.includedInPrice ? "rgba(22,163,74,0.2)" : "rgba(91,80,214,0.15)"}`,
                    whiteSpace: "nowrap", flexShrink: 0,
                  }}>
                    {badgeLabel}
                  </span>
                )}

                <Toggle
                  checked={isActive}
                  onChange={() => setActiveMeals((prev) => ({ ...prev, [meal._id]: !prev[meal._id] }))}
                />
              </div>

              {/* Per-day blocks — only when active */}
              {isActive && (<div>
                {dates.map((date, di) => {
                  const isLast = di === dates.length - 1;

                  if (isPerPerson) {
                    // ── Per-person stepper mode ────────────────────────────
                    const count = getPersonCount(meal._id, date);
                    const dayTotal = setting.includedInPrice ? 0 : count * setting.pricePerPerson!;

                    return (
                      <div
                        key={date}
                        style={{
                          padding: "10px 12px",
                          borderBottom: !isLast ? "1px solid #f5f3fb" : undefined,
                          display: "flex", alignItems: "center", gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b6880", flex: 1 }}>
                          {fmtDay(date)}
                        </span>

                        <StepBtn label="−" disabled={count === 0} onClick={() => changeCount(meal._id, date, -1)} />

                        <span style={{
                          minWidth: 52, textAlign: "center",
                          fontSize: 13, fontWeight: 700, color: count > 0 ? "#1a1520" : "#c4c0d0",
                        }}>
                          {count} os.
                        </span>

                        <StepBtn label="+" disabled={count >= capacity} onClick={() => changeCount(meal._id, date, 1)} />

                        {count > 0 && (
                          <span style={{
                            fontSize: 12, fontWeight: 700, flexShrink: 0, minWidth: 52, textAlign: "right",
                            color: setting.includedInPrice ? "#16a34a" : "#5b50d6",
                          }}>
                            {setting.includedInPrice ? "ukl." : fmtPrice(dayTotal, currency)}
                          </span>
                        )}
                      </div>
                    );
                  }

                  // ── Item selection mode ──────────────────────────────────
                  const slots = getSlots(meal._id, date);
                  const dayTotal = slots.reduce((sum, itemId) => {
                    if (!itemId) return sum;
                    const item = meal.items.find((it) => (it._id || it.name) === itemId);
                    return sum + (item && !setting.includedInPrice ? item.price : 0);
                  }, 0);
                  const orderedCount = slots.filter(Boolean).length;

                  return (
                    <div
                      key={date}
                      style={{
                        padding: "10px 12px",
                        borderBottom: !isLast ? "1px solid #f5f3fb" : undefined,
                      }}
                    >
                      {/* Day label + total */}
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        marginBottom: 8,
                      }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b6880" }}>
                          {fmtDay(date)}
                        </span>
                        {orderedCount > 0 && (
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            color: setting.includedInPrice ? "#16a34a" : "#5b50d6",
                          }}>
                            {setting.includedInPrice
                              ? `${orderedCount} os. — uključeno`
                              : `${orderedCount} os. — ${fmtPrice(dayTotal, currency)}`}
                          </span>
                        )}
                      </div>

                      {/* Guest slots */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {slots.map((itemId, idx) => {
                          const selectedItem = itemId
                            ? meal.items.find((it) => (it._id || it.name) === itemId)
                            : null;
                          const unitPrice = selectedItem && !setting.includedInPrice
                            ? selectedItem.price : 0;

                          return (
                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{
                                fontSize: 11, color: "#9893a8", fontWeight: 600,
                                minWidth: 48, flexShrink: 0,
                              }}>
                                👤 G{idx + 1}
                              </span>

                              <div style={{ position: "relative", flex: 1 }}>
                                <select
                                  value={itemId}
                                  onChange={(e) => setSlot(meal._id, date, idx, e.target.value)}
                                  style={{
                                    width: "100%", height: 34,
                                    padding: "0 28px 0 10px",
                                    border: `1.5px solid ${itemId ? "#5b50d6" : "#e8e5f0"}`,
                                    borderRadius: 8,
                                    background: itemId ? "#f8f7ff" : "#fff",
                                    color: itemId ? "#1a1520" : "#9893a8",
                                    fontSize: 12, fontFamily: "inherit",
                                    appearance: "none", cursor: "pointer", outline: "none",
                                  }}
                                >
                                  <option value="">— Preskačem —</option>
                                  {availableItems.map((item) => {
                                    const id = item._id || item.name;
                                    const priceLabel = setting.includedInPrice
                                      ? "uključeno"
                                      : item.price === 0 ? "besplatno"
                                      : `${fmtPrice(item.price, currency)}`;
                                    return (
                                      <option key={id} value={id}>
                                        {item.name} — {priceLabel}
                                      </option>
                                    );
                                  })}
                                </select>
                                <span style={{
                                  position: "absolute", right: 8, top: "50%",
                                  transform: "translateY(-50%)", pointerEvents: "none",
                                  color: "#9893a8", fontSize: 10,
                                }}>▾</span>
                              </div>

                              {itemId && (
                                <span style={{
                                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                                  color: unitPrice === 0 ? "#16a34a" : "#5b50d6",
                                  minWidth: 36, textAlign: "right",
                                }}>
                                  {unitPrice === 0 ? "ukl." : fmtPrice(unitPrice, currency)}
                                </span>
                              )}

                              {slots.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeGuest(meal._id, date, idx)}
                                  style={{
                                    width: 26, height: 26, border: "1px solid #fca5a5",
                                    borderRadius: 6, background: "#fff5f5", color: "#dc2626",
                                    fontSize: 13, cursor: "pointer", flexShrink: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    lineHeight: 1,
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          );
                        })}

                        {slots.length < capacity && (
                          <button
                            type="button"
                            onClick={() => addGuest(meal._id, date)}
                            style={{
                              alignSelf: "flex-start",
                              marginLeft: 54,
                              height: 28, padding: "0 12px",
                              border: "1.5px dashed #c4c0d0",
                              borderRadius: 8, background: "transparent",
                              color: "#9893a8", fontSize: 11, fontWeight: 600,
                              cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                            }}
                          >
                            + Dodaj gosta
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MealSelector;
